import { useMemo } from 'react';
import cx from 'classnames';
import { getTableQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { DATE_QUERY_PARAMS, DEFAULT_QUERY_PARAM_TABLE_IDENTIFIER, FilterKeys } from '../constants';
import {
  countNestedFilters,
  countRegularFilters,
  createFilterText,
  getOptionsAndLabels
} from '../utils';
import styles from '../index.module.scss';
import ActiveFilterButton from './ActiveFilterButton';
import InactiveFilterButton from './InactiveFilterButton';
import { IFilterButton, IFilterCount } from '../types';

const FilterButton = (props: IFilterButton) => {
  const {
    filterSchemaMap,
    filterSchema,
    clearAllFilters,
    queryParamTableIdentifier = DEFAULT_QUERY_PARAM_TABLE_IDENTIFIER,
    selectedFiltersWrapperContainer,
    openFilterDrawer,
    isFilterButtonDisabled = false
  } = props;

  const queryParams = getTableQueryParams(filterSchema, queryParamTableIdentifier, true);

  const calculateFilterCounts = (): { filterCounts: IFilterCount[]; totalCount: number } => {
    let totalActiveFiltersCount = 0;
    const filterCounts: IFilterCount[] = [];

    filterSchemaMap.forEach((filterKey: FilterKeys) => {
      if (DATE_QUERY_PARAMS.includes(filterKey)) return;

      const { label } = getOptionsAndLabels(filterSchema, filterKey);
      const activeFilters = queryParams?.[filterKey];
      const isNested = filterSchema?.[filterKey]?.nestedFilter;

      const filterCount = isNested
        ? countNestedFilters(activeFilters)
        : countRegularFilters(activeFilters);

      if (filterCount > 0) {
        totalActiveFiltersCount += filterCount;
        filterCounts.push({ label, count: filterCount, filterKey });
      }
    });

    return { filterCounts, totalCount: totalActiveFiltersCount };
  };

  const handleClearAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearAllFilters(true);
  };

  const filterButton = useMemo(() => {
    const { filterCounts } = calculateFilterCounts();
    const filterText = createFilterText(filterCounts);
    const hasActiveFilters = filterCounts.length > 0;

    return (
      <div className={cx(styles.selectedFiltersWrapper, selectedFiltersWrapperContainer)}>
        {hasActiveFilters ? (
          <ActiveFilterButton
            filterCounts={filterCounts}
            filterText={filterText}
            onOpenDrawer={openFilterDrawer}
            onClearAll={handleClearAllClick}
            isDisabled={isFilterButtonDisabled}
          />
        ) : (
          <InactiveFilterButton
            onOpenDrawer={openFilterDrawer}
            isDisabled={isFilterButtonDisabled}
          />
        )}
      </div>
    );
  }, [
    filterSchema,
    queryParams,
    filterSchemaMap,
    openFilterDrawer,
    clearAllFilters,
    isFilterButtonDisabled,
    selectedFiltersWrapperContainer
  ]);

  return <>{filterButton}</>;
};

export default FilterButton;
